import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const premiumCardVariants = cva(
  "rounded-xl border text-card-foreground transition-all duration-200",
  {
    variants: {
      variant: {
        // Default premium card
        default: "bg-card border-border shadow-premium hover:shadow-lg",
        
        // Glass morphism effect
        glass: "glass border-white/20 backdrop-blur-xl",
        
        // Dark glass for dark themes
        "glass-dark": "glass-dark border-white/10 backdrop-blur-xl",
        
        // Elevated with stronger shadow
        elevated: "bg-card border-border shadow-lg hover:shadow-xl hover:shadow-primary/5 hover:border-primary/20",
        
        // Interactive card with hover effects
        interactive: "bg-card border-border shadow-premium hover:shadow-lg hover:scale-[1.01] hover:border-primary/30 cursor-pointer",
        
        // Gradient border
        gradient: "bg-card border-transparent bg-gradient-to-r from-primary/20 via-transparent to-accent/20 shadow-premium hover:shadow-lg",
        
        // Success themed
        success: "bg-card border-success/20 shadow-premium hover:shadow-lg hover:shadow-success/10",
        
        // Warning themed  
        warning: "bg-card border-warning/20 shadow-premium hover:shadow-lg hover:shadow-warning/10",
        
        // Accent themed
        accent: "bg-card border-accent/20 shadow-premium hover:shadow-lg hover:shadow-accent/10",
        
        // Flat minimal
        flat: "bg-card border-border",
        
        // Outlined
        outlined: "bg-transparent border-2 border-border hover:bg-card/50",
      },
      padding: {
        none: "p-0",
        sm: "p-4",
        default: "p-6", 
        lg: "p-8",
        xl: "p-10",
      },
      glow: {
        none: "",
        subtle: "hover:shadow-lg",
        medium: "hover:shadow-xl hover:shadow-primary/10",
        strong: "shadow-lg shadow-primary/10 hover:shadow-2xl hover:shadow-primary/20",
      }
    },
    defaultVariants: {
      variant: "default",
      padding: "default",
      glow: "none",
    },
  }
)

export interface PremiumCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof premiumCardVariants> {}

const PremiumCard = React.forwardRef<HTMLDivElement, PremiumCardProps>(
  ({ className, variant, padding, glow, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(premiumCardVariants({ variant, padding, glow, className }))}
      {...props}
    />
  )
)
PremiumCard.displayName = "PremiumCard"

const PremiumCardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5", className)}
    {...props}
  />
))
PremiumCardHeader.displayName = "PremiumCardHeader"

const PremiumCardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn("font-semibold leading-none tracking-tight text-lg", className)}
    {...props}
  />
))
PremiumCardTitle.displayName = "PremiumCardTitle"

const PremiumCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground leading-relaxed", className)}
    {...props}
  />
))
PremiumCardDescription.displayName = "PremiumCardDescription"

const PremiumCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("pt-0", className)} {...props} />
))
PremiumCardContent.displayName = "PremiumCardContent"

const PremiumCardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center pt-6 border-t border-border/50", className)}
    {...props}
  />
))
PremiumCardFooter.displayName = "PremiumCardFooter"

export {
  PremiumCard,
  PremiumCardHeader,
  PremiumCardFooter,
  PremiumCardTitle,
  PremiumCardDescription,
  PremiumCardContent,
  premiumCardVariants,
} 